import {loadCSS, loadJS} from "./helpers";
import markerDefaultSVG from '../../../assets/images/pin.svg';
import markerGoldSVG from '../../../assets/images/pin-active-gold.svg';
import markerActiveSVG from '../../../assets/images/pin-active.svg';

function flyToLocation(map, latlng, zoom = 17) {
    const dest = L.latLng(latlng);
    const lat = Number(dest.lat);
    const lng = Number(dest.lng);
    const destZoom = Number(zoom);

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(destZoom)) {
        return;
    }

    const target = L.latLng(lat, lng);
    map.stop();

    const size = map.getSize();
    if (!size.x || !size.y) {
        map.invalidateSize();
        map.setView(target, destZoom, { animate: false });
        return;
    }

    let current;
    let currentZoom;
    try {
        current = map.getCenter();
        currentZoom = map.getZoom();
    } catch (err) {
        map.setView(target, destZoom, { animate: false });
        return;
    }

    if (!Number.isFinite(current.lat) || !Number.isFinite(current.lng) || !Number.isFinite(currentZoom)) {
        map.setView(target, destZoom, { animate: false });
        return;
    }

    const zoomDiff = Math.abs(destZoom - currentZoom);
    const samePoint = current.distanceTo(target) < 1;

    // Leaflet flyTo produces NaN when the center does not change and the zoom
    // jump is large, and an explicit duration keeps that broken animation running.
    // MapLibre GL Leaflet can also desync during flyTo's per-frame projection.
    if (samePoint || zoomDiff >= 10) {
        map.setView(target, destZoom, { animate: true });
        return;
    }

    map.flyTo(target, destZoom, {
        animate: true,
        duration: 1.5,
    });
}

export async function initMap() {

    if ( !document.querySelector('[data-leaflet-map]')) {
        return;
    }

    let assetsLoaded = false;
    try {
        await loadCSS("https://unpkg.com/leaflet@1.9.4/dist/leaflet.css");
        await loadJS("https://unpkg.com/leaflet@1.9.4/dist/leaflet.js");
        await loadCSS("https://unpkg.com/leaflet-gesture-handling/dist/leaflet-gesture-handling.min.css");
        await loadJS("https://unpkg.com/leaflet-gesture-handling/dist/leaflet-gesture-handling.min.js");

        await loadCSS("https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css");
        await loadJS("https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js");
        await loadJS("https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.0.22/leaflet-maplibre-gl.js");

        assetsLoaded = true;
    } catch (err) {
        console.error("Failed to load Leaflet or plugin:", err);
    }

    if (!assetsLoaded) {
        return;
    }

    document.querySelectorAll('[data-leaflet-map]').forEach((element) => {

        let locations = element.dataset.map_locations;
        locations = locations ? JSON.parse(locations) : {};
        if (locations.length < 1) {
            return;
        }

        let type = element.dataset.map_type ? element.dataset.map_type : 'auto';
        let center = element.dataset.map_center;
        center = center ? center.split(',') : '';

        const map = L.map(element, {
            scrollWheelZoom: false,
            minZoom: 1,
            maxBounds: [[-85.051129, -180], [85.051129, 180]],
            maxBoundsViscosity: 1,
            gestureHandling: true,
            gestureHandlingOptions: {
                text: {
                    touch: "Bruk to fingre for \u00e5 flytte kartet",
                    scroll: "Hold ctrl-tasten inne og rull for \u00e5 zoome p\u00e5 kartet",
                    scrollMac: "Hold inne \u2318-tasten og rull for \u00e5 zoome p\u00e5 kartet"
                },
                locale: 'no',
            }
        });

        const centerLat = center.length === 2 ? parseFloat(center[0]) : NaN;
        const centerLng = center.length === 2 ? parseFloat(center[1]) : NaN;
        if (Number.isFinite(centerLat) && Number.isFinite(centerLng)) {
            map.setView([centerLat, centerLng], 6);
        } else {
            const first = locations.find((data) => (
                Number.isFinite(parseFloat(data['lat'])) && Number.isFinite(parseFloat(data['lng']))
            ));
            if (first) {
                map.setView([parseFloat(first['lat']), parseFloat(first['lng'])], 6);
            } else {
                map.setView([64.5, 11.5], 5);
            }
        }

        L.maplibreGL({ style: 'https://tiles.openfreemap.org/styles/positron' }).addTo(map);

        const pin = L.icon({
            iconUrl: markerDefaultSVG,
            iconSize: [30, 35],
            iconAnchor: [0, 35],
            popupAnchor: [15, -35],
        });

        const pin2 = L.icon({
            iconUrl: markerGoldSVG,
            iconSize: [40, 47],
            iconAnchor: [0, 47],
            popupAnchor: [20, -47],
        });

        const pin3 = L.icon({
            iconUrl: markerActiveSVG,
            iconSize: [40, 47],
            iconAnchor: [0, 47],
            popupAnchor: [20, -47],
        });

        const markers = [];
        locations.forEach((data, id) => {
            const lat = parseFloat(data['lat']);
            const lng = parseFloat(data['lng']);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return;
            }

            const marker = L.marker([lat, lng], {
                icon: type === 'partners-address' ? pin2 : pin,
                slug: data['slug'],
            });

            if (data['popup']) {
                marker.bindPopup(data['popup']);
            }

            marker.addTo(map);
            marker.on('click', function(e) {

                if (type === 'partners-address') {
                    markers.forEach((m) => m.setIcon(pin2));
                } else {
                    markers.forEach((m) => m.setIcon(pin));
                }
                e.target.setIcon(pin3);

                flyToLocation(map, marker.getLatLng(), 17);

                if (type === 'partners-address') {
                    document.querySelectorAll('.map-block .address-list .single-address').forEach((element, i) => {
                        element.classList.remove('selected');
                        if (i === id) {
                            element.classList.add('selected');
                        }
                    });
                } else if (type === 'address') {
                    document.querySelectorAll('.map-block .contacts-list .single-contact').forEach((element, i) => {
                        element.classList.add('-hidden');
                        if (i === id) {
                            element.classList.remove('-hidden');
                        }
                    });
                    document.querySelector('.map-block .form-col input').value = document.querySelector('.map-block .contacts-list .single-contact-' + id).dataset.name;
                } else if (type === 'priser') {
                    document.querySelectorAll('.price-map-block .form-col input, .price-map-block .form-col select').forEach((input) => {
                        input.value = data['name'];
                    });
                }
            });

            markers.push(marker);
        });

        if (type !== 'auto') {
            document.querySelectorAll('[name="address"]').forEach(function(address) {
                address.addEventListener('change', function(e) {
                    const parent = e.target.parentElement;
                    const select = parent.querySelector('select');
                    const id = select.options[select.selectedIndex].getAttribute('data-marker-id');

                    document.querySelectorAll('.map-block .contacts-list .single-contact').forEach((element, i) => {
                        element.classList.add('-hidden');
                        if (i == id) {
                            element.classList.remove('-hidden');
                        }
                    });

                    const selectedMarker = markers[id];
                    if (!selectedMarker) {
                        return;
                    }

                    markers.forEach((m) => m.setIcon(pin));
                    selectedMarker.setIcon(pin3);

                    flyToLocation(map, selectedMarker.getLatLng(), 17);

                    element.scrollIntoView({ behavior: 'smooth' });
                });
            });

            document.querySelectorAll('.redirect-to-department').forEach((button) => {
                button.addEventListener('click', function(e) {
                    const select = document.querySelector('.price-map-block .form-col select');
                    const option = select.options[select.selectedIndex];
                    location.href = option.dataset.url;
                });
            });
        }

        if (markers.length === 1) {
            map.setView(markers[0].getLatLng(), 16);
        } else if (markers.length) {
            map.fitBounds(L.featureGroup(markers).getBounds(), { padding: [50, 100] });
        }

        // Prevent scrolling when clicking zoom buttons.
        map.getContainer().focus = () => {}
    });
}